<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useFieldOrder } from 'dashboard/composables/useFieldOrder';
import { useCompaniesStore } from 'dashboard/stores/companies';
import { useDealsStore } from 'dashboard/stores/deals';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { useDealLabelsStore } from 'dashboard/stores/dealLabels';
import ContactAPI from 'dashboard/api/contacts';
import CompanyAPI from 'dashboard/api/companies';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import SourceAttributesSection from 'dashboard/components-next/Forms/SourceAttributesSection.vue';
import PipelineStageAccordion from 'dashboard/components-next/Deals/PipelineStageAccordion.vue';
import DealLabels from 'dashboard/components-next/Deals/DealLabels.vue';
import DealCustomAttributeItem from 'dashboard/components-next/Deals/DealCustomAttributeItem.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import CompanyCreateDialog from 'dashboard/components-next/Companies/CompanyCreateDialog.vue';

const props = defineProps({
  dealData: { type: Object, default: null },
  // 'create': o mesmo formulario cria a negociacao (menu esquerdo, sem dialogo)
  mode: { type: String, default: 'edit' },
  initialValues: { type: Object, default: null },
  defaultPipelineId: { type: Number, default: null },
  conversationId: { type: Number, default: null },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();
const store = useStore();
const dealsStore = useDealsStore();
const pipelinesStore = usePipelinesStore();
const dealLabelsStore = useDealLabelsStore();
const companiesStore = useCompaniesStore();

const isCreate = computed(() => props.mode === 'create');
const createContactDialogRef = ref(null);
const createCompanyDialogRef = ref(null);
const isCreatingCompany = computed(
  () => companiesStore.getUIFlags.creatingItem
);

const selectedLabels = ref([]);

const form = ref({
  name: '',
  value: '',
  currency: 'BRL',
  pipelineStageId: null,
  contactId: null,
  companyId: null,
  assignedToId: null,
  productsOfInterest: '',
  lastPurchaseAt: '',
  customAttributes: {},
});

const validationRules = computed(() =>
  isCreate.value
    ? {
        name: { required },
        contactId: { required },
        pipelineStageId: { required },
      }
    : { name: { required } }
);

const v$ = useVuelidate(validationRules, form);

const isFormInvalid = computed(() => v$.value.$invalid);

const { orderedFields } = useFieldOrder('deal');

const getNameErrorMessage = () =>
  v$.value.name.$error ? t('DEALS.CREATE.FIELDS.NAME_ERROR') : '';

// Options
const allPipelines = computed(() => pipelinesStore.getPipelinesList);

const activePipelineId = computed(() => {
  const stageId = form.value.pipelineStageId;
  if (!stageId) return props.defaultPipelineId;
  const pipeline = allPipelines.value.find(p =>
    (p.stages || []).some(s => s.id === stageId)
  );
  return pipeline?.id ?? null;
});

const agentOptions = computed(() => {
  const options = (store.getters['agents/getAgents'] || []).map(a => ({
    value: a.id,
    label: a.name,
  }));
  const assignedTo = props.dealData?.assignedTo;
  if (
    assignedTo?.id &&
    !options.some(option => option.value === assignedTo.id)
  ) {
    const name = assignedTo.name || String(assignedTo.id);
    options.push({
      value: assignedTo.id,
      label:
        assignedTo.active === false
          ? `${name} ${t('DEALS.DEACTIVATED_AGENT_SUFFIX')}`
          : name,
    });
  }
  return options;
});

const contactOptions = ref([]);
const companyOptions = ref([]);

const currencyOptions = [
  { value: 'BRL', label: 'BRL' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' },
];

const contactToOption = c => ({
  value: c.id,
  label: c.name || c.email || String(c.id),
});

const companyToOption = c => ({ value: c.id, label: c.name || String(c.id) });

const loadContacts = async () => {
  const {
    data: { payload },
  } = await ContactAPI.get(1, 'name');
  contactOptions.value = payload.map(contactToOption);
};

const loadCompanies = async () => {
  const {
    data: { payload },
  } = await CompanyAPI.get({ page: 1, sort: 'name' });
  companyOptions.value = payload.map(companyToOption);
};

const searchContacts = debounce(async query => {
  if (!query) {
    await loadContacts();
    return;
  }
  const {
    data: { payload },
  } = await ContactAPI.search(query, 1, 'name');
  contactOptions.value = payload.map(contactToOption);
}, 300);

const searchCompanies = debounce(async query => {
  if (!query) {
    await loadCompanies();
    return;
  }
  const {
    data: { payload },
  } = await CompanyAPI.search(query, 1, 'name');
  companyOptions.value = payload.map(companyToOption);
}, 300);

const handleDealAttributeUpdate = (attributeKey, value) => {
  form.value.customAttributes = {
    ...form.value.customAttributes,
    [attributeKey]: value,
  };
};

const customAttributeWithValue = attribute => ({
  ...attribute,
  value: form.value.customAttributes?.[attribute.attributeKey] ?? '',
});

const ensureOptionPresent = (options, id, label) => {
  if (!id || options.value.some(option => option.value === id)) return;
  options.value = [{ value: id, label: label || String(id) }, ...options.value];
};

// last_purchase_at chega do backend como timestamp Unix (segundos),
// igual created_at/updated_at - precisa virar "YYYY-MM-DD" para o
// <input type="date">, usando os componentes locais (nao UTC) para
// não deslocar o dia por causa do fuso horário.
const timestampToDateInputValue = timestamp => {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const syncForm = deal => {
  if (!deal) return;
  const poi = Array.isArray(deal.productsOfInterest)
    ? deal.productsOfInterest.join(', ')
    : deal.productsOfInterest || '';
  form.value = {
    name: deal.name || '',
    value: deal.value ?? '',
    currency: deal.currency || 'BRL',
    pipelineStageId: deal.pipelineStageId || null,
    contactId: deal.contactId || null,
    companyId: deal.companyId || null,
    assignedToId: deal.assignedToId || null,
    productsOfInterest: poi,
    lastPurchaseAt: timestampToDateInputValue(deal.lastPurchaseAt),
    customAttributes: { ...(deal.customAttributes || {}) },
  };
  selectedLabels.value = (deal.labels || []).map(l => l.title);
  ensureOptionPresent(
    contactOptions,
    deal.contactId,
    deal.contact?.name || deal.contact?.email
  );
  ensureOptionPresent(companyOptions, deal.companyId, deal.company?.name);
};

watch(() => props.dealData, syncForm, { immediate: true });

const loadOptions = async () => {
  // allSettled: contas sem a feature Empresas recusam a listagem de empresas, e isso
  // nao pode impedir de reinserir o contato da negociacao nas opcoes
  await Promise.allSettled([
    loadContacts(),
    loadCompanies(),
    store.dispatch('agents/get'),
  ]);
  ensureOptionPresent(
    contactOptions,
    props.dealData?.contactId,
    props.dealData?.contact?.name || props.dealData?.contact?.email
  );
  ensureOptionPresent(
    companyOptions,
    props.dealData?.companyId,
    props.dealData?.company?.name
  );
};

// Criacao a partir de um contato/empresa/etapa: pre-seleciona e garante que
// o rotulo da opcao aparece mesmo fora da primeira pagina da busca.
const applyInitialValues = async () => {
  const initial = props.initialValues;
  if (!isCreate.value || !initial) return;
  if (initial.pipelineStageId) {
    form.value.pipelineStageId = initial.pipelineStageId;
  }
  if (initial.contactId) {
    form.value.contactId = initial.contactId;
    const {
      data: { payload: contact },
    } = await ContactAPI.show(initial.contactId);
    ensureOptionPresent(
      contactOptions,
      initial.contactId,
      contact?.name || contact?.email
    );
  }
  if (initial.companyId) {
    form.value.companyId = initial.companyId;
    const {
      data: { payload: company },
    } = await CompanyAPI.show(initial.companyId);
    ensureOptionPresent(companyOptions, initial.companyId, company?.name);
  }
};

onMounted(async () => {
  await loadOptions();
  await applyInitialValues();
});

const resetValidation = () => {
  v$.value.$reset();
};

const parseProductsOfInterest = () =>
  form.value.productsOfInterest
    ? form.value.productsOfInterest
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    : [];

const createDeal = async () => {
  try {
    const created = await dealsStore.create({
      name: form.value.name.trim(),
      value: form.value.value ? Number(form.value.value) : null,
      currency: form.value.currency || 'BRL',
      contact_id: form.value.contactId,
      company_id: form.value.companyId || null,
      pipeline_stage_id: form.value.pipelineStageId,
      assigned_to_id: form.value.assignedToId || null,
      products_of_interest: parseProductsOfInterest(),
      last_purchase_at: form.value.lastPurchaseAt || null,
      custom_attributes: form.value.customAttributes || {},
      labels: selectedLabels.value,
      conversation_id: props.conversationId || null,
    });
    useAlert(t('DEALS.CREATE.MESSAGES.SUCCESS'));
    emit('saved', created);
    return created;
  } catch {
    useAlert(t('DEALS.CREATE.MESSAGES.ERROR'));
    return null;
  }
};

const save = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return null;
  if (isCreate.value) return createDeal();
  if (!props.dealData?.id) return null;
  const poi = parseProductsOfInterest();
  const updated = await dealsStore.update({
    id: props.dealData.id,
    name: form.value.name.trim() || undefined,
    value: form.value.value !== '' ? Number(form.value.value) : undefined,
    currency: form.value.currency || undefined,
    pipeline_stage_id: form.value.pipelineStageId || undefined,
    contact_id: form.value.contactId || undefined,
    company_id: form.value.companyId || undefined,
    assigned_to_id: form.value.assignedToId || undefined,
    products_of_interest: poi,
    last_purchase_at: form.value.lastPurchaseAt || undefined,
    custom_attributes: form.value.customAttributes || {},
  });
  await dealLabelsStore.updateDealLabels(
    props.dealData.id,
    selectedLabels.value
  );
  const labels = dealLabelsStore.getLabels
    .filter(l => selectedLabels.value.includes(l.title))
    .map(l => ({ title: l.title, color: l.color }));
  const result = { ...updated, labels };
  emit('saved', result);
  return result;
};

const handleContactCreated = async contact => {
  try {
    const newContact = await store.dispatch('contacts/create', contact);
    await loadContacts();
    if (newContact?.id) {
      ensureOptionPresent(contactOptions, newContact.id, newContact.name);
      form.value.contactId = newContact.id;
    }
    createContactDialogRef.value?.onSuccess();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SUCCESS_MESSAGE')
    );
  } catch {
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ERROR_MESSAGE')
    );
  }
};

const handleCompanyCreated = async company => {
  const { contactId, ...companyParams } = company;
  try {
    const newCompany = await companiesStore.create(companyParams);
    if (contactId) {
      try {
        await companiesStore.attachContactToCompany(newCompany.id, contactId);
      } catch {
        useAlert(t('COMPANIES.CREATE.MESSAGES.CONTACT_LINK_ERROR'));
      }
    }
    await loadCompanies();
    ensureOptionPresent(companyOptions, newCompany.id, newCompany.name);
    form.value.companyId = newCompany.id;
    createCompanyDialogRef.value?.onSuccess();
    useAlert(t('COMPANIES.CREATE.MESSAGES.SUCCESS'));
  } catch {
    useAlert(t('COMPANIES.CREATE.MESSAGES.ERROR'));
  }
};

defineExpose({ save, loadOptions, isFormInvalid, resetValidation });
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
      <template v-for="field in orderedFields" :key="field.id">
        <div :class="{ 'sm:col-span-2': field.fullWidth }">
          <DealLabels
            v-if="field.type === 'standard' && field.key === 'labels'"
            v-model:selected-labels="selectedLabels"
          />
          <div
            v-else-if="field.type === 'standard' && field.key === 'contact'"
            class="flex flex-col gap-1"
          >
            <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
              t('DEALS.CREATE.FIELDS.CONTACT')
            }}</span>
            <ComboBox
              v-model="form.contactId"
              :options="contactOptions"
              :placeholder="t('DEALS.CREATE.FIELDS.CONTACT_PLACEHOLDER')"
              :has-error="isCreate && v$.contactId.$error"
              :message="
                isCreate && v$.contactId.$error
                  ? t('DEALS.CREATE.FIELDS.CONTACT_ERROR')
                  : ''
              "
              @search="searchContacts"
              @update:model-value="isCreate && v$.contactId.$touch()"
            />
            <button
              v-if="isCreate"
              type="button"
              class="self-start text-xs text-n-brand hover:underline"
              @click="createContactDialogRef?.dialogRef.open()"
            >
              {{ t('DEALS.CREATE.FIELDS.CREATE_CONTACT_ACTION') }}
            </button>
          </div>
          <div
            v-else-if="field.type === 'standard' && field.key === 'company'"
            class="flex flex-col gap-1"
          >
            <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
              t('DEALS.CREATE.FIELDS.COMPANY')
            }}</span>
            <ComboBox
              v-model="form.companyId"
              :options="companyOptions"
              :placeholder="t('DEALS.CREATE.FIELDS.COMPANY_PLACEHOLDER')"
              @search="searchCompanies"
            />
            <button
              v-if="isCreate"
              type="button"
              class="self-start text-xs text-n-brand hover:underline"
              @click="createCompanyDialogRef?.dialogRef.open()"
            >
              {{ t('DEALS.CREATE.FIELDS.CREATE_COMPANY_ACTION') }}
            </button>
          </div>
          <PipelineStageAccordion
            v-else-if="
              field.type === 'standard' && field.key === 'pipeline_stage'
            "
            v-model="form.pipelineStageId"
            :pipelines="allPipelines"
            :active-pipeline-id="activePipelineId"
            :placeholder="t('DEALS.CREATE.FIELDS.STAGE_PLACEHOLDER')"
          />
          <Input
            v-else-if="field.type === 'standard' && field.key === 'name'"
            v-model="form.name"
            :label="t('DEALS.CREATE.FIELDS.NAME')"
            :placeholder="t('DEALS.CREATE.FIELDS.NAME_PLACEHOLDER')"
            :message="getNameErrorMessage()"
            :message-type="v$.name.$error ? 'error' : 'info'"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
            @input="v$.name.$touch()"
            @blur="v$.name.$touch()"
          />
          <Input
            v-else-if="field.type === 'standard' && field.key === 'value'"
            v-model="form.value"
            type="number"
            :label="t('DEALS.CREATE.FIELDS.VALUE')"
            :placeholder="t('DEALS.CREATE.FIELDS.VALUE_PLACEHOLDER')"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
          />
          <div
            v-else-if="field.type === 'standard' && field.key === 'currency'"
            class="flex flex-col gap-1"
          >
            <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
              t('DEALS.CREATE.FIELDS.CURRENCY')
            }}</span>
            <ComboBox
              v-model="form.currency"
              :options="currencyOptions"
              :placeholder="t('DEALS.CREATE.FIELDS.CURRENCY_PLACEHOLDER')"
            />
          </div>
          <div
            v-else-if="field.type === 'standard' && field.key === 'assigned_to'"
            class="flex flex-col gap-1"
          >
            <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
              t('DEALS.CREATE.FIELDS.ASSIGNED_TO')
            }}</span>
            <ComboBox
              v-model="form.assignedToId"
              :options="agentOptions"
              :placeholder="t('DEALS.CREATE.FIELDS.ASSIGNED_TO_PLACEHOLDER')"
            />
          </div>
          <Input
            v-else-if="
              field.type === 'standard' && field.key === 'products_of_interest'
            "
            v-model="form.productsOfInterest"
            :label="t('DEALS.CREATE.FIELDS.PRODUCTS_OF_INTEREST')"
            :placeholder="
              t('DEALS.CREATE.FIELDS.PRODUCTS_OF_INTEREST_PLACEHOLDER')
            "
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
          />
          <Input
            v-else-if="
              field.type === 'standard' && field.key === 'last_purchase_at'
            "
            v-model="form.lastPurchaseAt"
            type="date"
            :label="t('DEALS.CREATE.FIELDS.LAST_PURCHASE_AT')"
            :placeholder="t('DEALS.CREATE.FIELDS.LAST_PURCHASE_AT_PLACEHOLDER')"
            custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
          />
          <DealCustomAttributeItem
            v-else-if="field.type === 'custom'"
            :attribute="customAttributeWithValue(field.attribute)"
            @update="handleDealAttributeUpdate"
          />
        </div>
      </template>
    </div>

    <!-- Section: Informações de origem -->
    <SourceAttributesSection v-model="form.customAttributes" />

    <template v-if="isCreate">
      <CreateNewContactDialog
        ref="createContactDialogRef"
        @create="handleContactCreated"
      />
      <CompanyCreateDialog
        ref="createCompanyDialogRef"
        :is-loading="isCreatingCompany"
        @create="handleCompanyCreated"
      />
    </template>
  </div>
</template>
